/**
 * Alert Notifications v2 — mirrors pom-2-api's
 * src/types/notificationRules.types.ts.
 *
 * Every routing sentence the Safety Hub shows is built from this payload, not
 * written by hand in a component, so the explanation cannot drift from what
 * the API actually does.
 */

export type NotificationAlertType = 'SOS' | 'CheckOnMe' | 'Shift' | 'RiskReview';

export interface RoutingPerson {
    userId: string;
    name: string;
    email: string | null;
    methods: string[];
    reachable: boolean;
}

export interface RoutingRule {
    alertType: NotificationAlertType;
    teamRouting: boolean;
    stages: string[];
    /** Every moment this alert type could notify at. */
    stageOptions: string[];
    stagesEditable: boolean;
    stageSettingKey: string | null;
    stageValue: string | null;
    named: RoutingPerson[];
    /** True while the organization has never saved this rule. */
    isImplicit: boolean;
    /**
     * Faults in the answers given, phrased for an admin. These never change
     * what happens at runtime — an alert that resolves to nobody still rolls
     * up — they exist so a half-answered rule looks wrong instead of finished.
     */
    problems: string[];
}

export interface RoutingBackstop {
    kind: 'point-of-contact' | 'admin-fallback' | 'none';
    person: RoutingPerson | null;
    adminCount: number;
}

export interface RoutingOverview {
    customerId: string;
    pointOfContact: RoutingPerson | null;
    backstop: RoutingBackstop;
    teamStructure: {
        teamCount: number;
        teamsWithoutLeader: number;
        leaders: RoutingPerson[];
        subscribers: RoutingPerson[];
        unreachableLeaders: RoutingPerson[];
    };
    dispatch: { enabled: boolean; phones: string[]; emails: string[] };
    /** Everyone who can be named on a rule, with their reachability. */
    systemUsers: RoutingPerson[];
    rules: RoutingRule[];
    excludedAlertTypes: string[];
    supportedAlertTypes: NotificationAlertType[];
}

export const ALERT_TYPE_LABEL: Record<NotificationAlertType, string> = {
    SOS: 'SOS',
    CheckOnMe: 'Check On Me',
    Shift: 'Shift',
    RiskReview: 'Risk Review',
};

/**
 * Plain words for the organization's Check On Me notification setting
 * (`checkOnMe.leader.notifications.mode`), shared by the Notifications page and
 * the Settings page so both describe it the same way.
 */
export const CHECK_ON_ME_WHEN: Record<string, string> = {
    NONE: 'Never',
    EXPIRED: 'When it expires',
    ESCALATED: 'When it escalates',
    EXPIRED_ESCALATED: 'When it expires or escalates',
    ALL: 'At every step, from start to check out',
};
